import { apiFetch } from "@/client/lib/api";
import type { PropagationStatus } from "@/shared/propagation";

/**
 * Typed client for the tenant nodes endpoint. Reads are open to any member; all
 * requests are tenant-scoped server-side (RLS), so the client never passes a
 * tenant id. NetBox is the source of truth; the portal serves a synced snapshot.
 */

/** A port list as it appears folded into a node's public IP (PE-920). */
export interface NodePublicIpPortList {
  id: string;
  name: string | null;
  ports: string[];
}

/**
 * A node's public IP + propagation state (PE-920 / PE-924). Derived from the
 * node's port-list mappings: present while the node has at least one mapping,
 * with `ports` the union of all mapped lists.
 */
export interface NodePublicIp {
  status: PropagationStatus;
  /** The provisioned public IP; null until verified. */
  publicIp: string | null;
  /** Union of all mapped lists' port tokens, normalized and ascending. */
  ports: string[];
  /** The port lists mapped onto this node. */
  portLists: NodePublicIpPortList[];
  requestedAt: string;
  /** When the row last changed (a re-map/edit bumps it) - drives "pending since". */
  updatedAt: string;
  verifiedAt: string | null;
}

export interface Node {
  id: string;
  nodeType: string;
  gpuType: string | null;
  status: string | null;
  statusLabel: string | null;
  site: string | null;
  rack: string | null;
  serial: string | null;
  internalIp: string | null;
  /**
   * Assigned public IP from NetBox (PE-933), read-only, or null until Mantle
   * assigns one. Always paired with `publicPorts` (both-or-neither).
   */
  publicIpAddress: string | null;
  /** Forwarded public ports as normalized tokens; empty unless `publicIpAddress` is set. */
  publicPorts: string[];
  /**
   * Derived port-list public-IP state (PE-920). Dormant: superseded by the
   * NetBox-driven `publicIpAddress`/`publicPorts` display and only used behind
   * the `publicPorts` feature flag.
   */
  publicIp: NodePublicIp | null;
  syncedAt: string;
}

export interface NodesResponse {
  nodes: Node[];
  netboxConfigured: boolean;
}

export const nodesApi = {
  list: () => apiFetch<NodesResponse>("/nodes"),
  get: (id: string) => apiFetch<{ node: Node }>(`/nodes/${id}`).then((r) => r.node),
  /**
   * Map one or more port lists onto one or more nodes. This is what gives a node
   * a public IP (recorded `pending`); gated server-side on the tenant having at
   * least one allowed source IP.
   */
  mapPublicPorts: (nodeIds: string[], portListIds: string[]) =>
    apiFetch<{ mapped: number }>("/nodes/public-ports", {
      method: "POST",
      json: { nodeIds, portListIds },
    }),
  /** Unmap a port list from a node (releases the IP if it was the last mapping). */
  unmapPublicPort: (id: string, portListId: string) =>
    apiFetch<void>(`/nodes/${id}/public-ports/${portListId}`, { method: "DELETE" }),
};
